import { FC, useEffect, useRef } from 'react';

import { useActionInput } from '@bonusly/common-action-input';

import { Bonus } from '../../../../../types/bonus';
import { HorizontalPrompts } from '../../../../prompt-chips/prompt-chips';
import { SpacedSpinner } from '../../components/common/loading';
import { useBonusAnalytics } from '../../hooks/use-bonus-analytics';
import { useSuggestedResponses } from './hooks/use-suggested-responses';

interface SuggestedCommentsProps {
	bonus: Bonus;
	onSetText: (s: string) => void;
	onSuggestionCleared?: () => void;
}

export const SuggestedComments: FC<SuggestedCommentsProps> = ({ bonus, onSetText, onSuggestionCleared }) => {
	const { isLoading, suggestions } = useSuggestedResponses(bonus.id);
	const { state } = useActionInput();
	const { trackSuggestedThankYouShown, trackClickSuggestedThankYou, trackSuggestedThankYouDismissed } = useBonusAnalytics();

	const hasText = (state.plainText ?? '').trim().length > 0;
	const hasTrackedShown = useRef(false);
	const wasVisible = useRef(false);
	const lastDismissReason = useRef<'chip-selected' | null>(null);

	useEffect(() => {
		if (!hasText && suggestions.length > 0 && !isLoading) {
			if (!wasVisible.current) {
				onSuggestionCleared?.();
			}
			wasVisible.current = true;
			lastDismissReason.current = null;

			if (!hasTrackedShown.current) {
				trackSuggestedThankYouShown({ suggestionsCount: suggestions.length });
				hasTrackedShown.current = true;
			}
		}

		if (hasText && wasVisible.current) {
			if (lastDismissReason.current !== 'chip-selected') {
				trackSuggestedThankYouDismissed();
			}
			wasVisible.current = false;
		}

		if (!hasText && (suggestions.length === 0 || isLoading) && wasVisible.current) {
			wasVisible.current = false;
		}
	}, [hasText, suggestions, isLoading, trackSuggestedThankYouShown, trackSuggestedThankYouDismissed, onSuggestionCleared]);

	if (hasText) return null;
	if (isLoading) return <SpacedSpinner my={2} />;
	if (suggestions.length === 0) return null;

	const handleSelect = (text: string, index: number) => {
		lastDismissReason.current = 'chip-selected';
		trackClickSuggestedThankYou({
			suggestionText: text,
			suggestionIndex: index,
			suggestionsCount: suggestions.length,
		});
		onSetText(text);
	};

	return <HorizontalPrompts prompts={suggestions} onSelectPrompt={handleSelect} bleed={false} mb={0} />;
};
